import i18n from '@/i18n';
import { getApiBaseUrl, getApimSubscriptionKey } from '@/config/env';

/** The id that traces a response in VoteMate's logs: the API's, else Front Door's. */
export function correlationIdFrom(headers?: Headers | null): string | null {
  return headers?.get('x-correlation-id') || headers?.get('x-azure-ref') || null;
}

/**
 * A failed backend response, carrying the status so callers can distinguish
 * "too large" from "rate limited" from "server broke". The previous bare
 * `Error` threw that information away.
 */
export class ApiError extends Error {
  readonly status: number;
  /** The id this request carries in the API's logs, when the response named one. */
  readonly correlationId: string | null;
  readonly retryAfter: string | null;

  constructor(message: string, status: number, headers?: Headers | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.correlationId = correlationIdFrom(headers);
    this.retryAfter = headers?.get('retry-after') ?? null;
  }
}

export interface ApiClientOptions extends RequestInit {
  failMessage?: string;
  baseUrl?: string;
  accessToken?: string;
}

/**
 * Central HTTP client for all API calls. Handles URL resolution, auth headers,
 * JSON serialization, and error handling.
 *
 * @param endpoint Path to the endpoint (e.g. '/user/ballot')
 * @param options Fetch options plus baseUrl, accessToken, and failMessage.
 *   - baseUrl: defaults to backend URL (or APIM when configured). Pass '' for
 *     relative URLs (Express server).
 *   - accessToken: when provided, adds Authorization: Bearer header.
 */
export async function apiClient(endpoint: string, options?: ApiClientOptions): Promise<Response> {
  const { failMessage, baseUrl, accessToken, body, headers, method, ...fetchOptions } =
    options ?? {};

  const resolvedBase = baseUrl !== undefined ? baseUrl : getApiBaseUrl();
  const isBackendCall = baseUrl === undefined;
  const resolvedMethod = method ?? 'GET';
  // A multipart upload (the voice-note recording) carries its own content type
  // with a generated boundary, so the browser has to set the header itself and
  // the body has to reach fetch untouched.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const partnerKey = isBackendCall ? getApimSubscriptionKey() : undefined;

  const response = await fetch(`${resolvedBase}${endpoint}`, {
    ...fetchOptions,
    method: resolvedMethod,
    headers: {
      ...(headers as Record<string, string>),
      ...(['POST', 'PUT', 'PATCH'].includes(resolvedMethod) && !isFormData
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(partnerKey ? { 'Ocp-Apim-Subscription-Key': partnerKey } : {}),
    },
    ...(body ? { body: isFormData || typeof body === 'string' ? body : JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const error = new ApiError(
      failMessage ?? i18n.t('requestFailed', { ns: 'common' }),
      response.status,
      response.headers
    );
    throw error;
  }

  return response;
}
